import React, { Component } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { LoadingOverlay } from '../../../components/Loading';
import Page, { Back } from '../../../components/Page';
import Card from '../../../components/Card';
import Icon from '../../../components/Icons';
import { IconButtonLink } from '../../../components/Buttons';
import { H1, H2 } from '../../../components/Typography';
import { colors, distances } from '../../../styles/constants';

import Details from './Details';
import { Customer } from '../../types';
import { CustomerToken } from '../../../types/customers';
import DeleteCustomerModal from './DeleteCustomerModal';
import ChangeCustomerIdModal from './ChangeCustomerIdModal';

interface MatchParams {
    accountId: string;
    customerId: string;
}

export interface CustomerPageProps extends WithTranslation, RouteComponentProps<MatchParams> {
    getCustomer: (accountId: string, customer_id: string) => void;
    getCustomerTokens: (accountId: string, customer_id: string) => void;
    openDeleteCustomerModal: (accountId: string, customer_id: string) => void;
    closeDeleteCustomerModal: () => void;
    deleteCustomer: (accountId: string, customer_id: string) => void;
    changeCustomerId: (accountId: string, oldCustomerId: string, newCustomerId: string) => void;
    customer: Customer | undefined;
    tokens: CustomerToken[] | undefined;
    isLoading: boolean;
    isNotFound: boolean;
    deleteCustomerModalOpen: boolean;
    changeCustomerIdModalOpen: boolean;
    openChangeCustomerIdModal: () => void;
    closeChangeCustomerIdModal: () => void;
    changeCustomerIdError: string;
}

class CustomerPage extends Component<CustomerPageProps> {
    componentDidMount() {
        this.props.getCustomer(this.props.match.params.accountId, this.props.match.params.customerId);
        this.props.getCustomerTokens(this.props.match.params.accountId, this.props.match.params.customerId);
    }

    componentDidUpdate(prevProps: Readonly<CustomerPageProps>, prevState: Readonly<{}>, snapshot?: any): void {
        if (this.props.match.params.customerId !== prevProps.match.params.customerId) {
            this.props.getCustomer(this.props.match.params.accountId, this.props.match.params.customerId);
            this.props.getCustomerTokens(this.props.match.params.accountId, this.props.match.params.customerId);
        }
    }

    render() {
        const {
            t,
            customer,
            tokens,
            isLoading,
            deleteCustomer,
            deleteCustomerModalOpen,
            openDeleteCustomerModal,
            closeDeleteCustomerModal,
            changeCustomerIdModalOpen,
            openChangeCustomerIdModal,
            closeChangeCustomerIdModal,
            isNotFound,
            changeCustomerId,
            changeCustomerIdError,
        } = this.props;
        const { accountId } = this.props.match.params;
        return (
            <>
                <Page
                    title={
                        <React.Fragment>
                            <Back />
                            <Title>{t('customers.customer.title')}</Title>
                        </React.Fragment>
                    }
                    hasEnvFrame={true}
                >
                    <Card title={undefined} noPadding>
                        <Wrapper>
                            {customer && (
                                <Details
                                    customer={customer}
                                    accountId={accountId}
                                    tokens={tokens}
                                    openDeleteCustomerModal={openDeleteCustomerModal}
                                    openChangeCustomerIdModal={openChangeCustomerIdModal}
                                />
                            )}
                            {isNotFound && !customer && (
                                <Padding>
                                    <H2>{t('customers.customer.not_found')}</H2>
                                    <br />
                                    <IconButtonLink
                                        to={`/${accountId}/customers`}
                                        icon={<Icon icon="arrow_left" fill="#ffffff" />}
                                        placement="left"
                                    >
                                        {t('customers.customer.back_to_customers')}
                                    </IconButtonLink>
                                </Padding>
                            )}

                            {isLoading && <LoadingOverlay />}
                        </Wrapper>
                    </Card>
                </Page>
                {deleteCustomerModalOpen && (
                    <DeleteCustomerModal
                        accountId={accountId}
                        customerId={this.props.match.params.customerId}
                        closeModal={closeDeleteCustomerModal}
                        deleteCustomer={deleteCustomer}
                    />
                )}
                {changeCustomerIdModalOpen && (
                    <ChangeCustomerIdModal
                        accountId={accountId}
                        customerId={this.props.match.params.customerId}
                        closeModal={closeChangeCustomerIdModal}
                        changeCustomerId={changeCustomerId}
                        changeCustomerIdError={changeCustomerIdError}
                    />
                )}
            </>
        );
    }
}

const Wrapper = styled.div`
    position: relative;
    min-height: calc(${distances.ginormous} + ${distances.large});
`;

const Padding = styled.div`
    padding: ${distances.normal};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const Title = styled(H1)`
    color: ${colors.text};
    display: inline-block;
`;

export default withTranslation()(CustomerPage);
